// src/app/Header.tsx
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {Menu} from "lucide-react";

import { useAccess } from "@/auth/active-project";
import { meQuery } from "@/auth/queries";
import { session } from "@/auth/session";
import { Button, GlowDot } from "@/design-system";
import { ROLE_LABEL } from "@/lib/labels";
import { useRealtimeStatus } from "@/realtime/RealtimeProvider";

import { FRONTEND_BUILD_VERSION } from "./buildVersion";
import { NotificationsButton } from "./NotificationsButton";
import { usePanelMode } from "./panel-mode";

const PILL = {
  live: { text: "متصل", tone: "ok", pulse: true },
  connecting: { text: "جارٍ الاتصال", tone: "warn", pulse: true },
  reconnecting: { text: "يعيد الاتصال", tone: "warn", pulse: true },
  stopped: { text: "غير متصل", tone: "bad", pulse: false },
} as const;

export function ConnectionPill() {
  const status = useRealtimeStatus();
  if (status === "idle") return null;
  const p = PILL[status];
  return (
    <div data-tone={p.tone} data-testid="connection-pill" role="status"
      className="flex items-center gap-[7px] rounded-full px-[11px] py-[5px] text-xs"
      style={{ border: "1px solid color-mix(in srgb, var(--fg) 35%, transparent)", color: "var(--fg)", background: "var(--bg)" }}>
      <GlowDot tone={p.tone} pulse={p.pulse} /> <span>{p.text}</span>
    </div>
  );
}

export function Header({ onMenu }: { onMenu: () => void }) {
  const navigate = useNavigate();
  const { data: me } = useQuery(meQuery);
  const { projects, active, isPlatformAdmin, roles, setActive } = useAccess();
  const hybrid = isPlatformAdmin && projects.length > 0;
  const mode = usePanelMode(hybrid ? "projects" : "platform");
  const showAdminLabel = isPlatformAdmin && (!hybrid || mode === "platform");
  const primary = showAdminLabel ? "ADMIN" : (["OWNER", "ACCOUNTANT", "BENEFICIARY", "TENANT"] as const).find((r) => roles.includes(r));

  return (
    <header className="sticky top-0 z-[5] flex flex-wrap items-center gap-3.5 border-b border-line px-[26px] py-3.5"
      style={{ background: "linear-gradient(180deg,rgba(13,32,52,.96),rgba(8,22,37,.88))", backdropFilter: "blur(10px)" }}>
      <button type="button" className="btn lg:!hidden" aria-label="القائمة" onClick={onMenu}><Menu size={16} /></button>
      <div className="flex flex-col leading-[1.3]">
        <strong className="text-sm font-semibold" data-testid="user-name">{me?.full_name ?? "…"}</strong>
        <span className="text-[11.5px] text-muted-2">{primary ? ROLE_LABEL[primary] : "مستخدم جديد"}</span>
      </div>
      {/* [جديد 3/11/2026 — "رقم إصدار نتأكد بيه إن التحديثات بتوصل"] رقم ثابت بيتزوّد يدوياً مع كل نسخة
          فرونت إند جديدة (buildVersion.ts) — لو اتغيّر بعد رفع زيب جديد، التحديث وصل فعلاً. */}
      <span data-testid="frontend-build-version" className="mono text-[10.5px] text-muted-3" title="رقم إصدار الفرونت إند الحالي">
        v{FRONTEND_BUILD_VERSION}
      </span>
      {projects.length > 0 && (!hybrid || mode === "projects") && (
        <select className="field !w-auto !py-1.5 text-[12.5px]" aria-label="المشروع النشط" value={active?.project_id}
          onChange={(e) => setActive(e.target.value)}>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
        </select>
      )}
      <div className="ms-auto flex items-center gap-[9px]">
        <ConnectionPill />
        <NotificationsButton />
        <Button onClick={() => void navigate({ to: "/security" })} data-testid="nav-security">الأمان</Button>
        <Button variant="outline" onClick={() => { void session.signOut(); void navigate({ to: "/login" }); }}>
          تسجيل خروج
        </Button>
      </div>
    </header>
  );
}
